import { useRef, useState, Fragment } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import QRCode from 'qrcode'
import {
  Package, Plus, Search, UserPlus, RotateCcw, History, Pencil, Trash2, X,
  Download, Upload, Printer, Loader2, ChevronDown, ChevronRight, Trash, User,
} from 'lucide-react'
import {
  listInventory,
  createItem,
  updateItem,
  retireItem,
  deleteItemPermanent,
  assignItem,
  returnItem,
  getItemHistory,
  exportInventory,
  analyzeImport,
  commitImport,
  bulkUpdate,
  bulkAssign,
  bulkRetire,
  bulkDelete,
  type InventoryItem,
  type InventoryItemCreate,
  type ItemCategory,
  type ItemStatus,
  type ItemCondition,
  type InventoryAssignment,
  type ImportAnalyzeResponse,
  type SmartImportResult,
  type BulkChanges,
  type SheetFormat,
} from '@/api/inventory'
import { listUsers } from '@/api/users'

const CATEGORIES: ItemCategory[] = ['laptop', 'monitor', 'phone', 'peripheral', 'furniture', 'other']
const STATUSES: ItemStatus[] = ['available', 'assigned', 'retired', 'repair']
const CONDITIONS: ItemCondition[] = ['new', 'good', 'worn', 'damaged']

type GroupMode = 'category' | 'holder' | 'none'
type ItemGroup = { key: string; label: string | null; holderId?: string | null; items: InventoryItem[] }

function buildGroups(items: InventoryItem[], mode: GroupMode): ItemGroup[] {
  if (mode === 'none') return [{ key: 'all', label: null, items }]
  const map = new Map<string, InventoryItem[]>()
  for (const i of items) {
    const key = mode === 'category' ? i.category : (i.current_holder_id || 'unassigned')
    ;(map.get(key) ?? map.set(key, []).get(key)!).push(i)
  }
  const entries = [...map.entries()]
  if (mode === 'category') {
    entries.sort((a, b) => CATEGORIES.indexOf(a[0] as ItemCategory) - CATEGORIES.indexOf(b[0] as ItemCategory))
    return entries.map(([key, groupItems]) => ({ key, label: key, items: groupItems }))
  }
  // Holder mode: keyed by user id, labelled by name, linkable to the profile.
  const groups = entries.map(([key, groupItems]) => ({
    key,
    label: key === 'unassigned' ? 'Unassigned' : (groupItems[0].current_holder_name || 'Unknown'),
    holderId: key === 'unassigned' ? null : key,
    items: groupItems,
  }))
  groups.sort((a, b) =>
    a.holderId == null ? 1 : b.holderId == null ? -1 : (a.label || '').localeCompare(b.label || ''))
  return groups
}

const STATUS_STYLES: Record<ItemStatus, string> = {
  available: 'bg-green-100 text-green-700',
  assigned: 'bg-blue-100 text-blue-700',
  retired: 'bg-gray-200 text-gray-600',
  repair: 'bg-amber-100 text-amber-700',
}

const emptyForm: InventoryItemCreate = {
  name: '',
  category: 'other',
  asset_tag: '',
  condition: null,
  quantity: 1,
  mac_address: '',
  notes: '',
  purchase_date: '',
  purchase_cost: '',
  vendor: '',
  warranty_expiry: '',
  image_url: '',
  document_url: '',
}

export default function Inventory() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ItemStatus | ''>('')
  const [categoryFilter, setCategoryFilter] = useState<ItemCategory | ''>('')

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<InventoryItem | null>(null)
  const [form, setForm] = useState<InventoryItemCreate & { status?: ItemStatus }>(emptyForm)
  const [error, setError] = useState('')

  const [assignItemTarget, setAssignItemTarget] = useState<InventoryItem | null>(null)
  const [assignUserId, setAssignUserId] = useState('')
  const [assignNote, setAssignNote] = useState('')

  const [historyItem, setHistoryItem] = useState<InventoryItem | null>(null)

  const { orgSlug } = useParams<{ orgSlug: string }>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [analysis, setAnalysis] = useState<ImportAnalyzeResponse | null>(null)
  const [smartResult, setSmartResult] = useState<SmartImportResult | null>(null)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkEdit, setBulkEdit] = useState<BulkChanges | null>(null)
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const [bulkAssignUser, setBulkAssignUser] = useState('')
  const [groupBy, setGroupBy] = useState<GroupMode>('category')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', 'all'],
    queryFn: () => listInventory({ per_page: 2000 }),
  })

  const { data: usersData } = useQuery({
    queryKey: ['users', 'active-all'],
    queryFn: () => listUsers({ is_active: true, per_page: 100 }),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventory'] })

  const createMutation = useMutation({
    mutationFn: (payload: InventoryItemCreate) => createItem(payload),
    onSuccess: () => { invalidate(); closeForm() },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to create item'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: InventoryItemCreate & { status?: ItemStatus } }) =>
      updateItem(id, payload),
    onSuccess: () => { invalidate(); closeForm() },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to update item'),
  })

  const retireMutation = useMutation({
    mutationFn: (id: string) => retireItem(id),
    onSuccess: invalidate,
  })

  const assignMutation = useMutation({
    mutationFn: ({ id, userId, note }: { id: string; userId: string; note?: string }) =>
      assignItem(id, userId, note),
    onSuccess: () => { invalidate(); closeAssign() },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to assign item'),
  })

  const returnMutation = useMutation({
    mutationFn: (id: string) => returnItem(id),
    onSuccess: invalidate,
  })

  const permanentDeleteMutation = useMutation({
    mutationFn: (id: string) => deleteItemPermanent(id),
    onSuccess: invalidate,
  })

  const clearSelection = () => setSelected(new Set())
  const afterBulk = () => { invalidate(); clearSelection() }

  const bulkUpdateMutation = useMutation({
    mutationFn: (changes: BulkChanges) => bulkUpdate([...selected], changes),
    onSuccess: () => { afterBulk(); setBulkEdit(null) },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Bulk update failed'),
  })
  const bulkAssignMutation = useMutation({
    mutationFn: (userId: string) => bulkAssign([...selected], userId),
    onSuccess: () => { afterBulk(); setBulkAssignOpen(false); setBulkAssignUser('') },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Bulk assign failed'),
  })
  const bulkRetireMutation = useMutation({
    mutationFn: () => bulkRetire([...selected]),
    onSuccess: afterBulk,
  })
  const bulkDeleteMutation = useMutation({
    mutationFn: () => bulkDelete([...selected]),
    onSuccess: afterBulk,
  })

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const currentFilters = () => ({
    search: search || undefined,
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
  })

  const exportMutation = useMutation({
    mutationFn: (format: SheetFormat) => exportInventory(currentFilters(), format),
  })

  const analyzeMutation = useMutation({
    mutationFn: (file: File) => analyzeImport(file),
    onSuccess: (data) => setAnalysis(data),
    onError: (e: any) => { setPendingFile(null); setError(e?.response?.data?.detail || 'Could not read the file') },
  })

  const commitMutation = useMutation({
    mutationFn: ({ file, mapping }: { file: File; mapping: any }) => commitImport(file, mapping),
    onSuccess: (res) => { invalidate(); setAnalysis(null); setPendingFile(null); setSmartResult(res) },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Import failed'),
  })

  function onImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file
    if (file) { setError(''); setPendingFile(file); analyzeMutation.mutate(file) }
  }

  function cancelImport() { setAnalysis(null); setPendingFile(null) }

  async function printLabels(list: InventoryItem[]) {
    const tagged = list.filter((i) => i.asset_tag)
    if (tagged.length === 0) { alert('No items with an asset ID to print.'); return }
    const labels = await Promise.all(
      tagged.map(async (i) => {
        // QR points at the public asset page so a scan opens meaningful details.
        // Prefer a configured public origin (e.g. https://app.managition.com) so
        // scanned labels resolve regardless of where they were printed from.
        const base = (import.meta.env.VITE_APP_URL || window.location.origin).replace(/\/$/, '')
        const url = `${base}/${orgSlug}/i/${encodeURIComponent(i.asset_tag as string)}`
        const qr = await QRCode.toDataURL(url, { margin: 1, width: 240 })
        return labelHtml(i, qr, orgSlug)
      }),
    )
    const win = window.open('', '_blank', 'width=520,height=600')
    if (!win) { alert('Please allow pop-ups to print labels.'); return }
    win.document.write(labelDocument(labels))
    win.document.close()
  }

  function openCreate() {
    setEditing(null)
    setForm(emptyForm)
    setError('')
    setShowForm(true)
  }

  function openEdit(item: InventoryItem) {
    setEditing(item)
    setForm({
      name: item.name,
      category: item.category,
      asset_tag: item.asset_tag || '',
      status: item.status,
      condition: item.condition || null,
      quantity: item.quantity,
      mac_address: item.mac_address || '',
      notes: item.notes || '',
      purchase_date: item.purchase_date || '',
      purchase_cost: item.purchase_cost ?? '',
      vendor: item.vendor || '',
      warranty_expiry: item.warranty_expiry || '',
      image_url: item.image_url || '',
      document_url: item.document_url || '',
    })
    setError('')
    setShowForm(true)
  }

  function closeForm() { setShowForm(false); setEditing(null); setError('') }
  function closeAssign() { setAssignItemTarget(null); setAssignUserId(''); setAssignNote(''); setError('') }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    // Normalize empty strings → null; cost → number
    const payload: InventoryItemCreate & { status?: ItemStatus } = {
      ...form,
      asset_tag: form.asset_tag || null,
      mac_address: form.mac_address || null,
      notes: form.notes || null,
      purchase_date: form.purchase_date || null,
      purchase_cost: form.purchase_cost === '' || form.purchase_cost == null ? null : Number(form.purchase_cost),
      vendor: form.vendor || null,
      warranty_expiry: form.warranty_expiry || null,
      image_url: form.image_url || null,
      document_url: form.document_url || null,
    }
    if (editing) {
      updateMutation.mutate({ id: editing.id, payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const items = data?.items ?? []
  const users = usersData?.items ?? []

  // Client-side filtering — search matches anything (name, tag, category, MAC,
  // holder, vendor, notes, status).
  const q = search.trim().toLowerCase()
  const filtered = items.filter((i) => {
    if (statusFilter && i.status !== statusFilter) return false
    if (categoryFilter && i.category !== categoryFilter) return false
    if (!q) return true
    return [i.name, i.asset_tag, i.category, i.mac_address, i.current_holder_name, i.vendor, i.notes, i.status]
      .filter(Boolean).join(' ').toLowerCase().includes(q)
  })
  const groups = buildGroups(filtered, groupBy)
  const selectedItems = filtered.filter((i) => selected.has(i.id))
  const allFilteredSelected = filtered.length > 0 && filtered.every((i) => selected.has(i.id))
  const usersById = new Map(users.map((u) => [u.id, u]))

  // Groups start collapsed (show just the category/person rows); a search forces
  // everything open so matches are visible.
  const isGroupOpen = (key: string) => !!q || expanded.has(key)
  const toggleGroup = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  const setMany = (ids: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)))
      return next
    })

  const showCat = groupBy !== 'category'
  const showHolder = groupBy !== 'holder'
  const colCount = 5 + (showCat ? 1 : 0) + (showHolder ? 1 : 0)

  const renderRow = (item: InventoryItem) => (
    <tr key={item.id} className={`hover:bg-gray-50 ${selected.has(item.id) ? 'bg-orange-50/50' : ''}`}>
      <td className="pl-4 pr-1 py-2.5">
        <input type="checkbox" aria-label={`Select ${item.name}`} checked={selected.has(item.id)} onChange={() => toggleRow(item.id)} />
      </td>
      <td className="px-4 py-2.5">
        <div className="font-medium text-gray-900">{item.name}</div>
        {(item.quantity > 1 || item.mac_address) && (
          <div className="text-xs text-gray-400 flex flex-wrap gap-x-2">
            {item.quantity > 1 && <span>Qty {item.quantity}</span>}
            {item.mac_address && <span className="font-mono">{item.mac_address}</span>}
          </div>
        )}
      </td>
      {showCat && <td className="px-4 py-2.5 capitalize text-gray-600">{item.category}</td>}
      <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{item.asset_tag || '—'}</td>
      <td className="px-4 py-2.5">
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[item.status]}`}>{item.status}</span>
      </td>
      {showHolder && <td className="px-4 py-2.5 text-gray-600">{item.current_holder_name || <span className="text-gray-300">—</span>}</td>}
      <td className="px-4 py-2.5">
        <div className="flex items-center justify-end gap-0.5">
          {item.status !== 'retired' && (
            item.current_holder_id ? (
              <button title="Return" onClick={() => returnMutation.mutate(item.id)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><RotateCcw className="h-4 w-4" /></button>
            ) : (
              <button title="Assign" onClick={() => { setAssignItemTarget(item); setError('') }} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><UserPlus className="h-4 w-4" /></button>
            )
          )}
          <button title="Print label" onClick={() => printLabels([item])} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Printer className="h-4 w-4" /></button>
          <button title="History" onClick={() => setHistoryItem(item)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><History className="h-4 w-4" /></button>
          <button title="Edit" onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Pencil className="h-4 w-4" /></button>
          {item.status !== 'retired' && (
            <button title="Retire" onClick={() => { if (confirm(`Retire "${item.name}"?`)) retireMutation.mutate(item.id) }} className="p-1.5 rounded hover:bg-amber-50 text-amber-500"><Trash2 className="h-4 w-4" /></button>
          )}
          <button title="Delete permanently" onClick={() => { if (confirm(`Permanently delete "${item.name}"? This removes it and its history and cannot be undone.`)) permanentDeleteMutation.mutate(item.id) }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash className="h-4 w-4" /></button>
        </div>
      </td>
    </tr>
  )

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="text-sm text-gray-500">Office assets and who currently holds them.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FormatMenu
            label="Export"
            icon={<Download className="h-4 w-4" />}
            disabled={exportMutation.isPending}
            onPick={(f) => exportMutation.mutate(f)}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={analyzeMutation.isPending}
            title="Import any spreadsheet — the columns are auto-detected"
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {analyzeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
          </button>
          <button
            onClick={() => printLabels(selectedItems)}
            disabled={selectedItems.length === 0}
            title={selectedItems.length === 0 ? 'Select items to print their labels' : `Print ${selectedItems.length} label(s)`}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Printer className="h-4 w-4" /> Print labels{selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white hover:bg-[#d54e22]"
          >
            <Plus className="h-4 w-4" /> Add Item
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={onImportFile}
            className="hidden"
          />
        </div>
      </div>

      {/* Filters + grouping */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search anything — item, person, tag, MAC, category…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ItemStatus | '')} className="rounded-lg border border-gray-300 px-3 py-2 text-sm capitalize">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value as ItemCategory | '')} className="rounded-lg border border-gray-300 px-3 py-2 text-sm capitalize">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
        </select>
        <div className="inline-flex rounded-lg border border-gray-300 bg-gray-50 p-0.5">
          {([['category', 'By category'], ['holder', 'By person'], ['none', 'List']] as [GroupMode, string][]).map(([m, l]) => (
            <button key={m} onClick={() => setGroupBy(m)} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${groupBy === m ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap rounded-lg border border-[#EC5B2C]/30 bg-orange-50 px-3 py-2 text-sm">
          <span className="font-medium text-gray-700">{selected.size} selected</span>
          <div className="flex-1" />
          <button onClick={() => setBulkEdit({})} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"><Pencil className="h-3.5 w-3.5" /> Edit</button>
          <button onClick={() => { setBulkAssignUser(''); setBulkAssignOpen(true) }} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5" /> Assign</button>
          <button onClick={() => printLabels(selectedItems)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"><Printer className="h-3.5 w-3.5" /> Labels</button>
          <button onClick={() => { if (confirm(`Retire ${selected.size} item(s)?`)) bulkRetireMutation.mutate() }} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Retire</button>
          <button onClick={() => { if (confirm(`Permanently delete ${selected.size} item(s)? This cannot be undone.`)) bulkDeleteMutation.mutate() }} className="rounded-lg border border-red-300 bg-white px-3 py-1.5 font-medium text-red-600 hover:bg-red-50 flex items-center gap-1.5"><Trash className="h-3.5 w-3.5" /> Delete</button>
          <button onClick={clearSelection} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Grouped table */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-12 text-center text-gray-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-12 text-center text-gray-400">
          {items.length === 0 ? 'No inventory items yet.' : 'No items match your search.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 text-gray-400 text-xs uppercase tracking-wide text-left">
                <tr>
                  <th className="pl-4 pr-1 py-2.5 w-8">
                    <input type="checkbox" aria-label="Select all" checked={allFilteredSelected} onChange={(e) => setMany(filtered.map((i) => i.id), e.target.checked)} />
                  </th>
                  <th className="px-4 py-2.5 font-medium">Item</th>
                  {showCat && <th className="px-4 py-2.5 font-medium">Category</th>}
                  <th className="px-4 py-2.5 font-medium">Asset Tag</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  {showHolder && <th className="px-4 py-2.5 font-medium">Held By</th>}
                  <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groups.map((g) => {
                  const open = g.label == null || isGroupOpen(g.key)
                  const holder = g.holderId ? usersById.get(g.holderId) : undefined
                  return (
                    <Fragment key={g.key}>
                      {g.label != null && (
                        <tr className="bg-gray-50/60 hover:bg-gray-100/60 cursor-pointer" onClick={() => toggleGroup(g.key)}>
                          <td colSpan={colCount} className="px-4 py-2 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                aria-label={`Select all in ${g.label}`}
                                checked={g.items.every((i) => selected.has(i.id))}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setMany(g.items.map((i) => i.id), e.target.checked)}
                              />
                              <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
                              {groupBy === 'holder' ? (
                                g.holderId ? (
                                  <>
                                    {holder?.avatar_url ? (
                                      <img src={holder.avatar_url} alt="" className="h-6 w-6 rounded-full object-cover" />
                                    ) : (
                                      <span className="h-6 w-6 rounded-full bg-[#EC5B2C]/15 text-[#EC5B2C] text-xs font-semibold flex items-center justify-center">
                                        {(g.label || '?').charAt(0).toUpperCase()}
                                      </span>
                                    )}
                                    <Link
                                      to={`/${orgSlug}/admin/users/${g.holderId}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="font-semibold text-gray-800 hover:text-[#EC5B2C] hover:underline"
                                    >{g.label}</Link>
                                  </>
                                ) : (
                                  <>
                                    <span className="h-6 w-6 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center"><User className="h-3.5 w-3.5" /></span>
                                    <span className="font-semibold text-gray-500">Unassigned</span>
                                  </>
                                )
                              ) : (
                                <>
                                  <Package className="h-4 w-4 text-gray-400" />
                                  <span className="font-semibold text-gray-800 capitalize">{g.label}</span>
                                </>
                              )}
                              <span className="text-xs text-gray-400">· {g.items.length}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {open && g.items.map(renderRow)}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <Modal title={editing ? 'Edit Item' : 'Add Item'} onClose={closeForm}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
            <Field label="Name" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as ItemCategory })} className={`${inputCls} capitalize`}>
                  {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </Field>
              <Field label="Asset Tag / ID">
                <input value={form.asset_tag || ''} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} placeholder="Auto-generated if left blank" className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Condition">
                <select value={form.condition || ''} onChange={(e) => setForm({ ...form, condition: (e.target.value || null) as ItemCondition | null })} className={`${inputCls} capitalize`}>
                  <option value="">—</option>
                  {CONDITIONS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </Field>
              <Field label="Quantity">
                <input type="number" min={0} value={form.quantity ?? 1} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} className={inputCls} />
              </Field>
            </div>
            <Field label="MAC Address">
              <input value={form.mac_address || ''} onChange={(e) => setForm({ ...form, mac_address: e.target.value })} placeholder="e.g. 7C-8A-E1-76-FF-8C" className={`${inputCls} font-mono`} />
            </Field>
            {editing && (
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ItemStatus })} className={`${inputCls} capitalize`}>
                  {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </Field>
            )}
            <div className="pt-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Financial</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Purchase Date"><input type="date" value={form.purchase_date || ''} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className={inputCls} /></Field>
              <Field label="Purchase Cost"><input type="number" step="0.01" min={0} value={form.purchase_cost ?? ''} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} className={inputCls} /></Field>
              <Field label="Vendor"><input value={form.vendor || ''} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={inputCls} /></Field>
              <Field label="Warranty Expiry"><input type="date" value={form.warranty_expiry || ''} onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} className={inputCls} /></Field>
            </div>
            <div className="pt-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Attachments</div>
            <Field label="Image URL"><input value={form.image_url || ''} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} /></Field>
            <Field label="Document URL"><input value={form.document_url || ''} onChange={(e) => setForm({ ...form, document_url: e.target.value })} className={inputCls} /></Field>
            <Field label="Notes"><textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} rows={2} /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={closeForm} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button>
              <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60">
                {editing ? 'Save' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Assign modal */}
      {assignItemTarget && (
        <Modal title={`Assign "${assignItemTarget.name}"`} onClose={closeAssign}>
          <div className="space-y-4">
            {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
            <Field label="Assign to user" required>
              <select value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)} className={inputCls}>
                <option value="">Select a user...</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </select>
            </Field>
            <Field label="Note (optional)">
              <input value={assignNote} onChange={(e) => setAssignNote(e.target.value)} className={inputCls} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={closeAssign} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button>
              <button
                disabled={!assignUserId || assignMutation.isPending}
                onClick={() => assignMutation.mutate({ id: assignItemTarget.id, userId: assignUserId, note: assignNote })}
                className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60"
              >Assign</button>
            </div>
          </div>
        </Modal>
      )}

      {/* History modal */}
      {historyItem && <HistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />}

      {/* Import preview modal (before writing) */}
      {analysis && (
        <Modal title="Review import" onClose={cancelImport}>
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${analysis.source === 'ai' ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-600'}`}>
                {analysis.source === 'ai' ? '✨ Mapped by AI' : 'Auto-detected'}
              </span>
              <span className="text-xs text-gray-500">
                Layout: <span className="font-medium text-gray-700">{analysis.preview.layout === 'per_holder' ? 'per employee' : 'per item'}</span>
              </span>
            </div>
            <div className="flex gap-3">
              <Stat label="Items" value={analysis.preview.items} tone="text-gray-900" />
              <Stat label="Assigned" value={analysis.preview.assigned} tone="text-blue-600" />
              <Stat label="Unmatched" value={analysis.preview.unmatched} tone={analysis.preview.unmatched ? 'text-amber-600' : 'text-gray-400'} />
            </div>
            {analysis.preview.note && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {analysis.preview.note}
              </p>
            )}
            {analysis.preview.unmatched > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {analysis.preview.unmatched} item(s) belong to people who don't match a user — they'll be created unassigned with the holder's name in a note.
              </p>
            )}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500">Preview (first {analysis.preview.sample.length})</div>
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                {analysis.preview.sample.map((s, i) => (
                  <div key={i} className="px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{s.name}</div>
                      <div className="text-xs text-gray-400 capitalize">{s.category}{s.mac_address ? ` · ${s.mac_address}` : ''}</div>
                    </div>
                    <div className="text-xs text-gray-500 shrink-0">{s.holder || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
            {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 px-3 py-2">{error}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={cancelImport} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600">Cancel</button>
              <button
                disabled={commitMutation.isPending || !pendingFile || analysis.preview.items === 0}
                onClick={() => pendingFile && commitMutation.mutate({ file: pendingFile, mapping: analysis.mapping })}
                className="px-4 py-2 rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60 flex items-center gap-2"
              >
                {commitMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Import {analysis.preview.items} item{analysis.preview.items === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Import result modal */}
      {smartResult && (
        <Modal title="Import complete" onClose={() => setSmartResult(null)}>
          <div className="space-y-4 text-sm">
            <div className="flex gap-3">
              <Stat label="Created" value={smartResult.created} tone="text-green-600" />
              <Stat label="Assigned" value={smartResult.assigned} tone="text-blue-600" />
              <Stat label="Unmatched" value={smartResult.unmatched} tone={smartResult.unmatched ? 'text-amber-600' : 'text-gray-400'} />
              <Stat label="Errors" value={smartResult.errors.length} tone={smartResult.errors.length ? 'text-red-600' : 'text-gray-400'} />
            </div>
            {smartResult.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 max-h-52 overflow-y-auto">
                <p className="font-medium text-red-700 mb-1">Rows that could not be imported:</p>
                <ul className="space-y-1 text-red-600">
                  {smartResult.errors.map((err, i) => (
                    <li key={i}>Row {err.row}: {err.message}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={() => setSmartResult(null)} className="px-4 py-2 rounded-lg bg-[#EC5B2C] text-white">Done</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk edit modal */}
      {bulkEdit && (
        <Modal title={`Edit ${selected.size} item(s)`} onClose={() => setBulkEdit(null)}>
          <div className="space-y-4">
            {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
            <p className="text-xs text-gray-500">Only the fields you set are changed; leave one blank to keep it as-is.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Status">
                <select value={bulkEdit.status || ''} onChange={(e) => setBulkEdit({ ...bulkEdit, status: (e.target.value || null) as ItemStatus | null })} className={`${inputCls} capitalize`}>
                  <option value="">— keep —</option>
                  {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select value={bulkEdit.category || ''} onChange={(e) => setBulkEdit({ ...bulkEdit, category: (e.target.value || null) as ItemCategory | null })} className={`${inputCls} capitalize`}>
                  <option value="">— keep —</option>
                  {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </Field>
              <Field label="Condition">
                <select value={bulkEdit.condition || ''} onChange={(e) => setBulkEdit({ ...bulkEdit, condition: (e.target.value || null) as ItemCondition | null })} className={`${inputCls} capitalize`}>
                  <option value="">— keep —</option>
                  {CONDITIONS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </Field>
              <Field label="Vendor">
                <input value={bulkEdit.vendor || ''} onChange={(e) => setBulkEdit({ ...bulkEdit, vendor: e.target.value })} placeholder="— keep —" className={inputCls} />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setBulkEdit(null)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button>
              <button
                disabled={bulkUpdateMutation.isPending}
                onClick={() => {
                  const changes: BulkChanges = {}
                  if (bulkEdit.status) changes.status = bulkEdit.status
                  if (bulkEdit.category) changes.category = bulkEdit.category
                  if (bulkEdit.condition) changes.condition = bulkEdit.condition
                  if (bulkEdit.vendor) changes.vendor = bulkEdit.vendor
                  if (Object.keys(changes).length === 0) { setError('Set at least one field'); return }
                  setError(''); bulkUpdateMutation.mutate(changes)
                }}
                className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60"
              >Apply</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk assign modal */}
      {bulkAssignOpen && (
        <Modal title={`Assign ${selected.size} item(s)`} onClose={() => setBulkAssignOpen(false)}>
          <div className="space-y-4">
            {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
            <Field label="Assign to user" required>
              <select value={bulkAssignUser} onChange={(e) => setBulkAssignUser(e.target.value)} className={inputCls}>
                <option value="">Select a user...</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </select>
            </Field>
            <p className="text-xs text-gray-500">Retired items in the selection are skipped.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setBulkAssignOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button>
              <button
                disabled={!bulkAssignUser || bulkAssignMutation.isPending}
                onClick={() => bulkAssignMutation.mutate(bulkAssignUser)}
                className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60"
              >Assign</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="flex-1 rounded-lg border border-gray-200 p-3 text-center">
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}

/** Small button that opens a CSV / Excel picker popover. */
function FormatMenu({ label, icon, onPick, disabled }: {
  label: string
  icon: React.ReactNode
  onPick: (format: SheetFormat) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
      >
        {icon} {label} <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-36 rounded-lg bg-white shadow-lg ring-1 ring-black/5 z-50 overflow-hidden">
            {(['csv', 'xlsx'] as SheetFormat[]).map((f) => (
              <button
                key={f}
                onClick={() => { setOpen(false); onPick(f) }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                {f === 'csv' ? 'CSV (.csv)' : 'Excel (.xlsx)'}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ---- Printable labels ------------------------------------------------------

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ))
}

function labelHtml(item: InventoryItem, qrDataUrl: string, orgSlug?: string): string {
  return `
    <div class="label">
      <img src="${qrDataUrl}" alt="QR" />
      <div class="meta">
        <div class="name">${escapeHtml(item.name)}</div>
        <div class="tag">${escapeHtml(item.asset_tag || '')}</div>
        <div class="scan">Scan for details</div>
        ${orgSlug ? `<div class="org">${escapeHtml(orgSlug)}</div>` : ''}
      </div>
    </div>`
}

function labelDocument(labels: string[]): string {
  // A4 sheet, 3 columns — fits many labels per page (no page-break per label).
  return `<!doctype html><html><head><title>Inventory labels</title><style>
    @page { size: A4; margin: 8mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: system-ui, -apple-system, sans-serif; }
    .sheet { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; }
    .label {
      height: 32mm; display: flex; align-items: center; gap: 2.5mm;
      padding: 2.5mm; overflow: hidden; border: 1px dashed #cbd5e1; border-radius: 2mm;
      break-inside: avoid; page-break-inside: avoid;
    }
    .label img { width: 25mm; height: 25mm; flex-shrink: 0; }
    .meta { min-width: 0; }
    .name { font-size: 9pt; font-weight: 600; line-height: 1.15;
      overflow: hidden; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; }
    .tag { font-size: 8.5pt; font-family: ui-monospace, monospace; margin-top: 1mm; word-break: break-all; }
    .scan { font-size: 6.5pt; color: #EC5B2C; font-weight: 600; margin-top: 1mm; }
    .org { font-size: 6pt; color: #666; text-transform: uppercase; letter-spacing: .05em; margin-top: .5mm; }
    @media screen { body { padding: 16px; background: #f3f4f6; } }
  </style></head><body>
    <div class="sheet">
      ${labels.join('\n')}
    </div>
    <script>
      window.onload = function () { window.focus(); window.print(); };
      window.onafterprint = function () { window.close(); };
    </script>
  </body></html>`
}

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3 sticky top-0 bg-white">
          <div className="flex items-center gap-2 font-semibold text-gray-900"><Package className="h-4 w-4 text-[#EC5B2C]" />{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

function HistoryModal({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['inventory-history', item.id],
    queryFn: () => getItemHistory(item.id),
  })
  const history: InventoryAssignment[] = data ?? []
  return (
    <Modal title={`History — ${item.name}`} onClose={onClose}>
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : history.length === 0 ? (
        <p className="text-sm text-gray-400">No assignment history.</p>
      ) : (
        <ul className="space-y-3">
          {history.map((h) => (
            <li key={h.id} className="flex items-start gap-3 text-sm">
              <span className={`mt-1 h-2 w-2 rounded-full ${h.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
              <div>
                <div className="font-medium text-gray-900">{h.user_name}{h.is_active && <span className="ml-2 text-xs text-green-600">(current)</span>}</div>
                <div className="text-xs text-gray-500">
                  {new Date(h.assigned_at).toLocaleDateString()} → {h.returned_at ? new Date(h.returned_at).toLocaleDateString() : 'now'}
                  {h.assigned_by_name && ` · by ${h.assigned_by_name}`}
                </div>
                {h.note && <div className="text-xs text-gray-400 italic">{h.note}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
